// §18.1 component inventory: "ConfirmDialog — one-line consequence statement; destructive-
// class actions require typing the amount or the word shown." Reason-based confirmation was
// built first (Reverse, WP-1.2); `requireTypedConfirmation` adds the "types amount" variant
// for write-off/waive (WP-1.8, RP-3).
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { useEffect, useState } from 'react';
import { ghostButtonInlineClass, inputClass, primaryButtonInlineClass } from './FormKit';

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  consequence,
  confirmLabel = 'Confirm',
  onConfirm,
  confirming = false,
  requireReason = false,
  reasonLabel = 'Reason',
  requireTypedConfirmation,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  consequence: string;
  confirmLabel?: string;
  onConfirm: (reason: string) => void;
  confirming?: boolean;
  requireReason?: boolean;
  reasonLabel?: string;
  /** The exact text the user must type to enable Confirm (e.g. the formatted amount). */
  requireTypedConfirmation?: string;
}): React.JSX.Element {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
      setTyped('');
    }
  }, [open]);

  const canConfirm =
    (!requireReason || reason.trim().length >= 10) &&
    (requireTypedConfirmation === undefined || typed === requireTypedConfirmation);

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <AlertDialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-[var(--radius-card)] border border-border bg-surface p-6 outline-none">
          <AlertDialog.Title className="mb-1 text-lg font-semibold text-ink">
            {title}
          </AlertDialog.Title>
          <AlertDialog.Description className="mb-4 text-sm text-muted">
            {consequence}
          </AlertDialog.Description>
          {requireReason && (
            <textarea
              aria-label={reasonLabel}
              className={inputClass}
              rows={2}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              placeholder={`${reasonLabel} (10+ characters)`}
            />
          )}
          {requireTypedConfirmation !== undefined && (
            <label className="mt-3 block text-sm text-muted" htmlFor="confirm-dialog-typed">
              Type &quot;{requireTypedConfirmation}&quot; to confirm
              <input
                id="confirm-dialog-typed"
                className={`${inputClass} mt-1`}
                value={typed}
                onChange={(e) => {
                  setTyped(e.target.value);
                }}
              />
            </label>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <AlertDialog.Cancel asChild>
              <button type="button" className={ghostButtonInlineClass}>
                Cancel
              </button>
            </AlertDialog.Cancel>
            <button
              type="button"
              className={primaryButtonInlineClass}
              disabled={!canConfirm || confirming}
              onClick={() => {
                onConfirm(reason);
              }}
            >
              {confirmLabel}
            </button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
